import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface LikedByUser {
  user_id: string;
  full_name?: string;
  email: string;
  avatar_url?: string;
}

interface VideoLikesData {
  likes: LikedByUser[];
  totalLikes: number;
}

interface UseVideoLikesResult {
  data: VideoLikesData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useVideoLikes = (videoId: string, limit: number = 5): UseVideoLikesResult => {
  const [data, setData] = useState<VideoLikesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchVideoLikes = useCallback(async () => {
    if (!videoId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Add auth header if user is logged in
      if (user) {
        const token = localStorage.getItem('supabase.auth.token');
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      }

      const response = await fetch(`/api/videos/${videoId}/likes?limit=${limit}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch video likes: ${response.statusText}`);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching video likes:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch video likes');
    } finally {
      setLoading(false);
    }
  }, [videoId, limit, user]);

  useEffect(() => {
    fetchVideoLikes();
  }, [fetchVideoLikes]);

  const refetch = () => {
    fetchVideoLikes();
  };

  return {
    data,
    loading,
    error,
    refetch,
  };
};